import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOvertimeApplications } from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Plus, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import type { OvertimeState } from '@saiens/shared/types';

const STATE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'f_approve', label: '待核准' },
  { value: 'agree', label: '已核准' },
  { value: 'refused', label: '已拒絕' },
];

const STATE_LABELS: Record<OvertimeState, string> = {
  draft: '草稿',
  f_approve: '待核准',
  agree: '已核准',
  refused: '已拒絕',
};

function StateIcon({ state }: { state: OvertimeState }) {
  switch (state) {
    case 'draft':
      return <FileText className="h-4 w-4 text-gray-400" />;
    case 'f_approve':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'agree':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'refused':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return null;
  }
}

export function OvertimeListPage() {
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState('');

  const { data: applicationsData, isLoading } = useOvertimeApplications({
    state: stateFilter || undefined,
    limit: 50,
  });

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">加班紀錄</h1>
        </div>
        <button
          onClick={() => navigate('/overtime/apply')}
          className="flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600"
        >
          <Plus className="h-4 w-4" />
          新增
        </button>
      </div>

      <div className="px-4 py-4">
        {/* State Filter */}
        <div className="mb-4 flex gap-2 overflow-x-auto">
          {STATE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStateFilter(f.value)}
              className={`flex-shrink-0 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                stateFilter === f.value
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Applications List */}
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : applicationsData && applicationsData.items.length > 0 ? (
          <div className="space-y-2">
            {applicationsData.items.map((app) => (
              <button
                key={app.id}
                onClick={() => navigate(`/overtime/${app.id}`)}
                className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <StateIcon state={app.state} />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {app.overtime_type_name}
                    </p>
                    <p className="text-xs text-gray-500">{app.start_day}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatWorkHours(app.duration_time)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {STATE_LABELS[app.state] || app.state}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">暫無加班紀錄</p>
        )}
      </div>
    </div>
  );
}
